import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useIsMobile } from '../hooks/useIsMobile';
import { getWorkers, setWorkerPaused, forgetWorker, getAgentTokens,
         issueAgentToken, revokeAgentToken, unbindAgentToken,
         deleteAgentToken, getAgentDownloads,
         createAgentDownloadTicket, clusterView } from '../api';
import ClusterShareSettings from './ClusterShareSettings';
import NodeSettingsModal from './NodeSettingsModal';
import NodeCommandModal from './NodeCommandModal';
import NodeFailuresModal from './NodeFailuresModal';
import StickyHScroll from './StickyHScroll';

// Адмін ходить у /api/admin/*, глядач — у /api/cluster-view/*: ті самі дані,
// але лише дії, які глядачеві дозволені (дивитись і видавати токени).
const ADMIN_API = { getWorkers, getAgentTokens, issueAgentToken,
                    getAgentDownloads, createAgentDownloadTicket };

/**
 * Сторінка /cluster (стара /workers — псевдонім) — стан машин, які рахують перебір.
 *
 * ЩО ТУТ ВАЖЛИВО РОЗУМІТИ, ДИВЛЯЧИСЬ НА ЦИФРИ:
 *
 * 1. Завантаженість процесора й памʼяті — це МАШИНА ЦІЛКОМ, а не наш контейнер.
 *    Вузол читає їх з /proc хоста, тож видно й те, що на машині робить хтось
 *    інший. Саме це число потрібне, щоб вирішити, чи можна дати вузлу більше
 *    роботи.
 * 2. «Початок обрахунків» і «час обрахунків» — про ПОТОЧНУ сесію. Машину
 *    вмикають і вимикають, і сума за тиждень не каже нічого про те, що
 *    відбувається зараз. Нова сесія починається, коли вузол бере блок після
 *    десяти хвилин простою.
 * 3. «Швидкість» — час на тисячу комбінацій. Це та сама швидкість, лише
 *    повернена тим боком, яким машини можна порівнювати на око.
 * 4. 🚨 Червоний відбиток даних означає, що машина рахує НЕ НА ТИХ ДАНИХ, і
 *    роботи вона не отримає взагалі. Без цього рядка вузол виглядав би як
 *    «просто нічого не робить», а причина була б невидима.
 */

const REFRESH_MS = 5000;
// Кожен шостий тик (30 с) — пакети й токени. Вони міняються раз на день, але
// переопитувати їх ТРЕБА: без цього невдалий перший запит лишався назавжди.
const SLOW_EVERY = 6;

// Сенсорний екран: основний вказівник — палець (телефон, планшет).
const isTouch = typeof window !== 'undefined' && !!window.matchMedia
  && window.matchMedia('(pointer: coarse)').matches;
const NO_STICK = { position: 'static', left: 'auto', boxShadow: 'none' };

const STATE = {
  busy:    { text: 'Рахує',      cls: 'ok'    },
  idle:    { text: 'Очікує',     cls: 'muted' },
  paused:  { text: 'Зупинено',   cls: 'warn'  },
  // Зупинено не адміністратором, а людиною за самою машиною (або її
  // розкладом). Стан окремий навмисно: інакше адмін тисне «Запустити», машина
  // мовчить, і причина невидима.
  local:   { text: 'Зайнята людиною', cls: 'warn' },
  offline: { text: 'Не на звʼязку', cls: 'bad' },
  // Машина не мовчить — вона ДАЛА НЕПРАВИЛЬНІ ЧИСЛА на перевірочній
  // комбінації, підкладеній у блок. Роботи більше не отримує, поки людина не
  // розбереться й не натисне «Запустити».
  quarantined: { text: 'Карантин', cls: 'bad' },
  // Сама НЕ оновиться: самооновлення не вдалось (або застарів координатор).
  // Лише тут потрібна дія людини, тому червоний.
  needs_update: { text: 'Потрібне втручання', cls: 'bad' },
  // Нова версія вже є, блоку машина не тримає — оновиться сама на найближчому
  // запиті роботи, за секунди. Людині робити нічого.
  updating: { text: 'Оновлюється', cls: 'warn' },
  // Доступ забрав адміністратор. Машина жива й стукає — саме тому стан окремий:
  // «Очікує» тут було б брехнею, а «Не на звʼязку» відправило б шукати мережу.
  revoked: { text: 'Доступ відкликано', cls: 'bad' },
};

// Коли і як оновиться машина, що відстала від чинного випуску (менеджер
// 03.11). Самооновлення пробується лише в мить запиту НОВОГО блоку, тож
// блок, що рахується, не переривається — машина дорахує, здасть і оновиться.
const UPDATE_PLAN = {
  after_block: {
    line: 'оновиться сама після цього блоку',
    hint: 'Є нова версія агента. Машина дорахує поточний блок, здасть його й '
        + 'оновиться сама (перезапуск лише процесу агента, секунди). Нічого '
        + 'робити не треба; блок не переривається, щоб не втратити його роботу.',
  },
  soon: {
    line: 'оновлюється сама — зачекайте хвилину',
    hint: 'Є нова версія агента, блоку машина не тримає — оновиться сама на '
        + 'найближчому запиті роботи. Нічого робити не треба.',
  },
  manual: {
    bad: true,
    line: 'сама не оновилась — перевстановіть агента',
    hint: 'Самооновлення не вдалось (причина — у бейджі «не оновилась сама»). '
        + 'Потрібно поставити свіжий пакет агента зі сторінки «Пакети агента»; '
        + 'токен і номер машини збережуться.',
  },
  server: {
    bad: true,
    line: 'агент новіший за сервер — оновіть сервер',
    hint: 'На машині випуск новіший, ніж на координаторі. Машини чіпати не '
        + 'треба — оновіть сервер.',
  },
};

function fmtGb(v) {
  return (v === null || v === undefined) ? '—' : Number(v).toFixed(1);
}

function fmtClock(ts) {
  if (!ts) return '—';
  const d = new Date(ts * 1000);
  return d.toLocaleString('uk-UA', { day: '2-digit', month: '2-digit',
                                     hour: '2-digit', minute: '2-digit' });
}

/** Як рядок показується: вимкнена машина — з нулями (див. коментар у рядку). */
function shown(raw) {
  return raw.state === 'offline'
    ? { ...raw, cpu_pct: 0, cpu_pct_ours: 0, mem_pct: 0, mem_used_gb: 0, mem_ours_gb: 0 }
    : raw;
}

/** Частка ДОЗВОЛЕНОГО нам (процесор і памʼять) — те саме число, що на смугах
 *  «наші». Одна функція і для клітинки, і для сортування, щоб вони не розійшлись. */
function ofAllowed(w) {
  const allowedProcs = w.procs_wanted || w.procs || null;
  const threads = w.threads || w.cores;
  const cpuAllowedPct = (allowedProcs && threads) ? 100 * allowedProcs / threads : null;
  const cpu = (cpuAllowedPct && w.cpu_pct_ours != null)
    ? Math.min(100, 100 * w.cpu_pct_ours / cpuAllowedPct) : null;
  const memBudget = w.mem_budget_gb != null ? w.mem_budget_gb : w.mem_avail_gb;
  const mem = (memBudget && w.mem_ours_gb != null) ? 100 * w.mem_ours_gb / memBudget : null;
  return { allowedProcs, threads, cpuAllowedPct, cpu, memBudget, mem };
}

// Порядок станів при сортуванні «Стан»: спершу ті, що працюють, далі ті, що
// потребують уваги, наприкінці — не на звʼязку.
const STATE_ORDER = ['busy', 'updating', 'idle', 'local', 'paused', 'needs_update',
                     'quarantined', 'revoked', 'offline'];

// Сортування колонок (менеджер 26.10). Значення — функція від рядка, яким він
// показується; null — «немає числа», такі рядки завжди внизу в обидва боки.
// `num` — числова колонка: перший клік дає найбільші зверху.
const SORTS = {
  name:    { get: w => (w.name || '').toLowerCase() },
  cpu:     { get: w => (w.cpu || '').toLowerCase() },
  state:   { get: w => { const i = STATE_ORDER.indexOf(w.state); return i < 0 ? 99 : i; } },
  cpu_pct: { get: w => w.cpu_pct, num: true },
  ours_cpu: { get: w => ofAllowed(w).cpu, num: true },
  mem_pct: { get: w => w.mem_pct, num: true },
  ours_mem: { get: w => ofAllowed(w).mem, num: true },
  mem_total: { get: w => w.mem_total_gb, num: true },
  started: { get: w => w.session_started || null, num: true },
  secs:    { get: w => w.session_secs, num: true },
  sph:     { get: w => perHour(w.secs_per_1k), num: true },
  // Швидкість: менше секунд на тисячу — швидша машина, тож «найкращі зверху»
  // означає зростання.
  per1k:   { get: w => w.secs_per_1k || null },
  wph:     { get: w => w.windows_per_hour, num: true },
  per1kw:  { get: w => secsPer1k(w.windows_per_hour) },
};
const SORT_KEY = 'cluster_sort_v1';

function readSort() {
  try {
    const v = JSON.parse(localStorage.getItem(SORT_KEY) || 'null');
    if (v && SORTS[v.key] && (v.dir === 1 || v.dir === -1)) return v;
  } catch (e) { /* приватне вікно */ }
  return null;
}

const CMD_NAMES = {
  restart_agent: 'перезапуск агента', reboot: 'перезавантаження', shutdown: 'вимкнення',
};

/** Рядок про команду з адмінки: чекає машину / виконано / не вдалось. Старі
 *  результати (понад добу) не показуємо — вони вже нічого не пояснюють. */
function cmdLine(c) {
  if (!c) return '';
  const what = CMD_NAMES[c.action] || c.action;
  if (c.state === 'pending') return `${what}: чекає машину`;
  if (c.at && Date.now() / 1000 - c.at > 86400) return '';
  if (c.state === 'done') return `${what}: ${c.msg || 'виконано'} · ${fmtClock(c.at)}`;
  return `${what}: не вдалось — ${c.msg || 'причина невідома'}`;
}

// Рідну горизонтальну смугу таблиці ховаємо — її заміняє StickyHScroll, що
// тримається внизу екрана. Вертикальна лишається (рядки гортаються в таблиці).
const HSCROLL_CSS = `
.hscroll-hidden::-webkit-scrollbar { height: 0; width: 8px; }
.hscroll-hidden::-webkit-scrollbar-thumb { background: #C5CBD3; border-radius: 4px; }
.hscroll-hidden::-webkit-scrollbar-track { background: transparent; }
.hscroll-hidden tbody td { border-bottom: 1px solid #F1F3F5; }
`;

const _plural = (v, one, few, many) => {
  const m10 = v % 10, m100 = v % 100;
  if (m10 === 1 && m100 !== 11) return one;
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return few;
  return many;
};

// «6 днів 23 години»; менше доби — «23 години 5 хв».
function fmtDaysHours(secs) {
  const s = Math.max(0, Math.round(secs || 0));
  const d = Math.floor(s / 86400), h = Math.floor((s % 86400) / 3600);
  const hh = `${h} ${_plural(h, 'година', 'години', 'годин')}`;
  if (d) return `${d} ${_plural(d, 'день', 'дні', 'днів')} ${hh}`;
  return `${hh} ${Math.floor((s % 3600) / 60)} хв`;
}

// «10:44 20.12» — місцевий час переглядача.
function fmtStamp(ts) {
  const t = new Date(ts * 1000);
  const p = (x) => String(x).padStart(2, '0');
  return `${p(t.getHours())}:${p(t.getMinutes())} ${p(t.getDate())}.${p(t.getMonth() + 1)}`;
}

function fmtDur(secs) {
  if (!secs && secs !== 0) return '—';
  const s = Math.max(0, Math.round(secs));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h) return `${h} год ${String(m).padStart(2, '0')} хв`;
  if (m) return `${m} хв ${String(s % 60).padStart(2, '0')} с`;
  return `${s} с`;
}

/** «Intel(R) Xeon(R) CPU E5-2680 v4 @ 2.40GHz» → «Intel Xeon E5-2680 v4 @ 2.40GHz».
 *  Службові позначки вендора з'їдали пів колонки і ламали рядок на шість. */
function shortCpu(v) {
  if (!v) return '—';
  return v.replace(/\((R|TM|tm)\)/g, '').replace(/\bCPU\b/g, '')
          .replace(/\s+/g, ' ').trim();
}

/** Час на 1000 комбінацій — головна колонка для порівняння машин. */
// Дві форми однієї швидкості: «штук за годину» і «час на тисячу штук».
function perHour(secsPer1k) {
  return secsPer1k ? Math.round(3600 * 1000 / secsPer1k) : null;
}
function secsPer1k(perHourRate) {
  return perHourRate ? 3600 * 1000 / perHourRate : null;
}

function fmtPer1k(secs) {
  if (!secs) return '—';
  if (secs >= 3600) return `${(secs / 3600).toFixed(1)} год`;
  if (secs >= 60)   return `${(secs / 60).toFixed(1)} хв`;
  return `${secs.toFixed(0)} с`;
}

/** Шкала + число ПОРУЧ, а не поверх.
 *
 * Перша версія малювала відсоток усередині смуги, і при завантаженні понад
 * ~35 % заливка наїжджала на текст — на скріні з бою «40%» читалось як «0%».
 */
function Bar({ pct, danger }) {
  const v = Math.max(0, Math.min(100, Number(pct) || 0));
  const known = pct !== null && pct !== undefined;
  const color = danger && v >= 90 ? '#C02B5E' : v >= 70 ? '#D97706' : '#007A41';
  return (
    <div style={S.barRow}>
      <div style={S.barWrap}>
        <div style={{ ...S.barFill, width: `${v}%`, background: color }} />
      </div>
      <span style={S.barText}>{known ? `${v.toFixed(0)}%` : '—'}</span>
    </div>
  );
}

const CLUSTER_HELP =
  'Вузли, що рахують перебір комбінацій. «CPU» і «Памʼять» — уся машина, разом із чужою роботою. Смуги «наші» показують інше: яку частку ТОГО, ЩО МАШИНА ДОЗВОЛЯЄ НАМ, ми справді використовуємо. Для процесора дозволене — це процеси, які дають ядра й повзунок на самій машині (по одному на фізичне ядро); для памʼяті — вільна памʼять без наших процесів мінус запас, лишений машині й людині. 100 % означає «взяли все, що можна». Якщо процесорна смуга помітно нижча за 100 % під час рахунку, причину написано під нею — найчастіше це памʼять. Зупинена машина далі показує стан, але роботи не отримує; блок, який вона вже рахує, доводиться до кінця. «Зайнята людиною» означає, що машину зупинили на ній самій або вона поза своїм розкладом — кнопка тут цього не скасовує.';

/* (i) поруч із заголовком: наведення на десктопі, дотик на телефоні.
   `text` — рядок або розмітка (абзаци, посилання). */
function InfoTip({ text }) {
  const [open, setOpen] = useState(false);
  return (
    <span
      style={{ position: 'relative', display: 'inline-flex' }}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        aria-label="Пояснення"
        onClick={() => setOpen(v => !v)}
        style={{
          width: 20, height: 20, borderRadius: '50%', border: '1.5px solid #94A3B8',
          background: '#fff', color: '#64748B', fontSize: 12, fontWeight: 700,
          lineHeight: '16px', padding: 0, cursor: 'pointer', fontFamily: 'Georgia, serif',
        }}
      >i</button>
      {open && (
        <span style={{
          position: 'absolute', top: 'calc(100% + 8px)', left: 0, zIndex: 50,
          width: 'min(560px, calc(100vw - 32px))', background: '#fff',
          border: '1px solid #E2E8F0', borderRadius: 10, padding: '12px 14px',
          boxShadow: '0 8px 24px rgba(15,23,42,0.14)', fontSize: 13,
          fontWeight: 400, lineHeight: 1.5, color: '#334155',
        }}>
          {text}
        </span>
      )}
    </span>
  );
}

/**
 * `viewer` — режим перегляду за паролем (системний адміністратор парку): без
 * зупинки машин, відкликання й видалення; «Додати машину» лишається.
 * `onUnauthorized` — сесія глядача стала недійсною (адмін змінив пароль чи
 * вимкнув перегляд) → повернутись до форми пароля.
 */
export default function Workers({ viewer = false, onUnauthorized } = {}) {
  const A = viewer ? clusterView : ADMIN_API;
  const isMobile = useIsMobile();
  // Закріплена «Машина» мусить бути ВУЖЧОЮ за екран: коли вона була ширшою
  // (довгий підпис у один рядок), на телефоні вона накривала весь екран і
  // таблиця «не гортались» — під нею їхали невидимі колонки (30.09).
  // Адмінові в першій колонці ще й галочка вибору — вона ширша.
  const COL1 = viewer ? 36 : 60;
  // 🚨 НА СЕНСОРНИХ ЕКРАНАХ «№» І «МАШИНА» НЕ ПРИЛИПАЮТЬ (29.10). Chrome на
  // Android/планшеті при прокрутці переставав малювати саме прилиплі клітинки
  // рядків (процесор поруч лишався). Не вилікували ні border-collapse:
  // separate, ні окремий шар (will-change) — перевірено на планшеті менеджера.
  // Ціна: при гортанні вбік назва машини їде разом із таблицею.
  const noStick = isTouch ? NO_STICK : null;
  const st1 = { ...S.stick1, minWidth: COL1, width: COL1, ...noStick };
  const stickM = { ...S.stick2, left: COL1, ...(isMobile ? S.stick2Mobile : null), ...noStick };
  const [data, setData]       = useState(null);
  const [error, setError]     = useState('');
  const [busyIds, setBusyIds] = useState([]);   // кнопки, що чекають відповіді
  const [tokens, setTokens]   = useState([]);
  const [newLabel, setNewLabel] = useState('');
  // Щойно виданий токен — у рамці над таблицею, поки адмін не сховає. З 19.10
  // він також лишається в таблиці (колонка «Токен») для перевстановлення.
  const [freshToken, setFreshToken] = useState(null);
  // Які токени в таблиці зараз розкриті (за замовчуванням текст прихований —
  // щоб не світився на скріншотах і з-за плеча).
  const [shownTok, setShownTok] = useState([]);
  const [copiedTok, setCopiedTok] = useState('');
  const [dist, setDist] = useState(null);
  const [downloading, setDownloading] = useState('');
  // Вибрані галочками машини (лише адмін) і вікно налаштувань для них.
  const [picked, setPicked] = useState([]);
  const [settingsFor, setSettingsFor] = useState(null);
  // Вікно «Керування»: перезапуск агента, перезавантаження, вимкнення.
  const [commandFor, setCommandFor] = useState(null);
  // Машина, чий журнал збоїв відкрито (клік по «збої: N»).
  const [failuresFor, setFailuresFor] = useState(null);
  // Сортування таблиці: {key, dir: 1|-1} або null (порядок сервера — за іменем).
  const [sort, setSort] = useState(readSort);
  const clickSort = (key) => {
    setSort(cur => {
      let next;
      if (!cur || cur.key !== key) next = { key, dir: SORTS[key].num ? -1 : 1 };
      else if (cur.dir === (SORTS[key].num ? -1 : 1)) next = { key, dir: -cur.dir };
      else next = null;                              // третій клік — як було
      try {
        if (next) localStorage.setItem(SORT_KEY, JSON.stringify(next));
        else localStorage.removeItem(SORT_KEY);
      } catch (e) { /* не біда */ }
      return next;
    });
  };
  // Контейнер таблиці — для плаваючої горизонтальної смуги (StickyHScroll).
  const tableRef = useRef(null);

  const load = useCallback(async (quiet) => {
    try {
      const res = await A.getWorkers();
      setData(res.data);
      setError('');
    } catch (e) {
      if (viewer && e?.response?.status === 401) { onUnauthorized?.(); return; }
      if (!quiet) setError(e?.response?.data?.detail || e.message || 'помилка запиту');
    }
  }, [A, viewer, onUnauthorized]);

  const loadTokens = useCallback(async () => {
    try {
      const res = await A.getAgentTokens();
      setTokens(res.data?.tokens || []);
    } catch (e) { /* список токенів не має ламати сторінку машин */ }
  }, [A]);

  const loadDist = useCallback(async () => {
    try {
      const res = await A.getAgentDownloads();
      setDist(res.data);
    } catch (e) { /* пакетів може ще не бути — сторінка від цього не ламається */ }
  }, [A]);

  useEffect(() => {
    load();
    loadTokens();
    loadDist();
    // 🚨 ПЕРЕОПИТУВАТИ ТРЕБА ВСЕ ТРИ, А НЕ ЛИШЕ ТАБЛИЦЮ. Спершу пакети й токени
    // питались один раз при відкритті, і сторінка, відкрита під час деплою,
    // НАЗАВЖДИ показувала «Пакети ще не викладені»: запит упав (поки бекенд
    // піднімався, проксі віддавав HTML), а повторити його було нікому. Таблиця
    // машин тим часом оговтувалась сама — тож сторінка виглядала живою і при
    // цьому брехала про головне. Знайдено 03.09 у браузері, не тестами.
    // Рідше за таблицю: пакети й токени міняються раз на день, не раз на секунду.
    let tick = 0;
    const t = setInterval(() => {
      load(true);
      if (++tick % SLOW_EVERY === 0) { loadDist(); loadTokens(); }
    }, REFRESH_MS);
    return () => clearInterval(t);
  }, [load, loadTokens, loadDist]);

  const download = async (fileName) => {
    // 🚨 БЕРЕМО ПЕРЕПУСТКУ, А НЕ САМ ФАЙЛ. Тут стояло качання в Blob — тобто
    // браузер тягнув усі 312 МБ у памʼять, мовчки й без смужки прогресу, і
    // лише потім пропонував зберегти. Для людини це виглядало як «натиснув, і
    // хвилин пʼять нічого не відбувається» (повідомлено 03.09). Маркер сесії —
    // це заголовок Authorization, посиланням його не передати, тому сервер
    // видає окрему коротку перепустку на один файл.
    setDownloading(fileName);
    try {
      const res = await A.createAgentDownloadTicket(fileName);
      window.location.href = res.data.url;
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setDownloading('');
    }
  };

  const addMachine = async () => {
    try {
      const res = await A.issueAgentToken(newLabel.trim());
      setFreshToken(res.data);
      setNewLabel('');
      await loadTokens();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    }
  };

  const toggleToken = async (tk) => {
    if (!tk.revoked_at && !window.confirm(
        `Відкликати доступ для «${tk.label}»? Машина перестане отримувати `
        + 'роботу на найближчому ж зверненні.')) return;
    try {
      await revokeAgentToken(tk.id, !tk.revoked_at);
      await Promise.all([loadTokens(), load(true)]);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    }
  };

  const releaseToken = async (tk) => {
    if (!window.confirm(`Відвʼязати «${tk.label}» від машини? Той самий токен `
                        + 'зможе прийняти нове залізо.')) return;
    try {
      await unbindAgentToken(tk.id);
      await loadTokens();
    } catch (e) { setError(e?.response?.data?.detail || e.message); }
  };

  const dropToken = async (tk) => {
    if (!window.confirm(`Видалити токен «${tk.label}» назовсім?`)) return;
    try {
      await deleteAgentToken(tk.id);
      await loadTokens();
    } catch (e) { setError(e?.response?.data?.detail || e.message); }
  };

  const toggle = async (w) => {
    setBusyIds(ids => [...ids, w.agent_id]);
    try {
      // Для машини в карантині «Запустити» = «я розібрався»: той самий виклик
      // знімає і паузу, і карантин (див. admin_worker_pause).
      await setWorkerPaused(w.agent_id, !(w.paused || w.quarantined));
      await load(true);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setBusyIds(ids => ids.filter(x => x !== w.agent_id));
    }
  };

  const forget = async (w) => {
    if (!window.confirm(`Прибрати «${w.name}» зі списку? `
                        + (w.state === 'offline'
                           ? 'Машина зараз не на звʼязку; якщо агент на ній колись '
                             + 'запуститься, вона зʼявиться знову.'
                           : 'Машина зараз НА ЗВʼЯЗКУ — агент зареєструється знову '
                             + 'за кілька секунд.'))) return;
    try {
      await forgetWorker(w.agent_id);
      await load(true);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    }
  };

  if (!data && !error) return <div style={S.wrap}><div style={S.info}>Завантаження…</div></div>;

  const workers = data?.workers || [];
  const serverFp = data?.fingerprint_id || '';
  const serverAgentV = data?.agent_version || 0;
  const busyN = workers.filter(w => w.state === 'busy').length;
  // Рядки таблиці в обраному порядку. Порожні значення — завжди внизу; при
  // рівних — порядок сервера (за іменем), щоб рядки не скакали між оновленнями.
  const rows = !sort ? workers : workers.map((w, i) => ({ w, i, v: SORTS[sort.key].get(shown(w)) }))
    .sort((a, b) => {
      const na = a.v === null || a.v === undefined || a.v === '';
      const nb = b.v === null || b.v === undefined || b.v === '';
      if (na !== nb) return na ? 1 : -1;
      if (!na && a.v !== b.v) return (a.v < b.v ? -1 : 1) * sort.dir;
      return a.i - b.i;
    }).map(x => x.w);
  // Заголовок, що сортує: клік — за зростанням/спаданням, третій — як було.
  const sortTh = (key, label, style, title) => {
    const on = sort && sort.key === key;
    return (
      <th style={{ ...S.th, ...style, cursor: 'pointer', userSelect: 'none',
                   ...(on ? { color: '#1A1A1A' } : null) }}
          title={(title ? `${title}\n\n` : '') + 'Клік — сортувати; третій клік — як було'}
          onClick={() => clickSort(key)}>
        {label}<span style={S.sortMark}>{on ? (sort.dir === 1 ? ' ▲' : ' ▼') : ' ↕'}</span>
      </th>
    );
  };
  // ОДНА ТАБЛИЦЯ ЗАМІСТЬ ДВОХ (менеджер 26.10). Токени, закріплені за машиною,
  // показуються в її рядку (колонка «Токен»); решта — ще не використані або
  // з машиною, яку прибрали хрестиком, — окремими рядками внизу.
  const tokByAgent = {};
  for (const tk of tokens) {
    if (tk.bound_agent) (tokByAgent[tk.bound_agent] = tokByAgent[tk.bound_agent] || []).push(tk);
  }
  const workerIds = new Set(workers.map(w => w.agent_id));
  const orphanTokens = tokens.filter(tk => !tk.bound_agent || !workerIds.has(tk.bound_agent));

  // Вміст клітинки «Токен»: стан, текст (прихований), коли виданий, дії адміна.
  const renderTok = (tk, showLabel) => (
    <div key={tk.id} style={S.tokCell}>
      <div style={S.tokLine}>
        <span style={{ ...S.badge, ...(tk.revoked_at ? S.badgeCls.bad : S.badgeCls.ok) }}>
          {tk.revoked_at ? 'Відкликано' : 'Чинний'}
        </span>
        {tk.token_plain ? (
          <>
            <code style={S.tokInline}>
              {shownTok.includes(tk.id) ? tk.token_plain : `${tk.token_plain.slice(0, 8)}••••`}
            </code>
            <button
              onClick={() => setShownTok(ids => ids.includes(tk.id)
                ? ids.filter(x => x !== tk.id) : [...ids, tk.id])}
              style={{ ...S.btn, ...S.btnGhost, ...S.btnSm, marginLeft: 0 }}>
              {shownTok.includes(tk.id) ? 'Сховати' : 'Показати'}
            </button>
            <button
              onClick={() => {
                navigator.clipboard?.writeText(tk.token_plain);
                setCopiedTok(tk.id);
                setTimeout(() => setCopiedTok(''), 1500);
              }}
              style={{ ...S.btn, ...S.btnGhost, ...S.btnSm, marginLeft: 0 }}>
              {copiedTok === tk.id ? 'Скопійовано' : 'Копіювати'}
            </button>
          </>
        ) : (
          <span style={S.sub}
                title="Токени, видані до 19.10, зберігались лише як хеш — їхній текст не відновити. Для перевстановлення видайте новий: машина з тим самим іменем отримає свій старий рядок у таблиці.">
            текст не збережено
          </span>
        )}
      </div>
      <div style={{ ...S.tokLine, marginTop: 3 }}>
        <span style={S.sub}>
          {showLabel ? `«${tk.label}» · ` : ''}видано {fmtClock(tk.created_at)}
        </span>
        {!viewer && (
          <>
            <button onClick={() => toggleToken(tk)}
                    style={{ ...S.btn, ...S.btnSm, marginLeft: 0,
                             ...(tk.revoked_at ? S.btnStart : S.btnStop) }}>
              {tk.revoked_at ? 'Повернути' : 'Відкликати'}
            </button>
            {tk.bound_agent && (
              <button onClick={() => releaseToken(tk)}
                      style={{ ...S.btn, ...S.btnGhost, ...S.btnSm, marginLeft: 0 }}
                      title="Той самий токен зможе прийняти нове залізо">
                Відвʼязати
              </button>
            )}
            <button onClick={() => dropToken(tk)}
                    style={{ ...S.btn, ...S.btnGhost, ...S.btnSm, marginLeft: 0 }}
                    title="Видалити токен назовсім">
              Видалити
            </button>
          </>
        )}
      </div>
    </div>
  );

  const inFlight = workers.reduce((a, w) => a + (w.block_size || 0), 0);
  // 🚨 ШВИДКІСТЬ ПАРКУ — З ЛІЧИЛЬНИКА ВИРІШЕНИХ КОМБІНАЦІЙ, А НЕ СУМА ШВИДКОСТЕЙ
  // МАШИН. Сума «комбінацій у блоці / тривалість блоку» по вузлах завищувала
  // в десятки разів (25.09: «1 тис. за 6.8 хв», тобто ~8.8 тис./год, а сітка
  // реально рухалась на 100-240 за годину): короткі блоки, де все відсіялось,
  // дають величезні швидкості, і середнє відношень — не відношення сум.
  // Середня = усе вирішене в сітці / активний час сітки; прогноз на добу,
  // тиждень і місяць — з неї. Остання = останні 1 тис. вирішених / їхній час.
  const grid = (data?.batches || []).find(b => b.speed) || null;
  const sp = grid?.speed || null;
  const n = (v) => Math.round(v).toLocaleString('uk-UA');
  const nowSec = Date.now() / 1000;
  const startedAt = grid?.started_at || null;
  // Завершення — за середньою швидкістю всіх уже вирішених комбінацій цього
  // перебору (avg_rate = вирішено / активний час).
  const finishAt = (sp?.avg_rate && sp.total > sp.done)
    ? nowSec + (sp.total - sp.done) / sp.avg_rate : null;
  const localN = workers.filter(w => w.state === 'local').length;
  const offlineN = workers.filter(w => w.state === 'offline').length;

  // ── Рядок підсумків під таблицею ─────────────────────────────────────────
  // Суми й середні — по машинах НА ЗВʼЯЗКУ: вимкнена машина несе останні
  // показники, які вже нічого не означають.
  const live = workers.filter(w => w.state !== 'offline');
  const sum = (arr, f) => arr.reduce((a, w) => a + (Number(f(w)) || 0), 0);
  const avg = (arr, f) => {
    const xs = arr.map(f).filter(v => v !== null && v !== undefined);
    return xs.length ? xs.reduce((a, v) => a + Number(v), 0) / xs.length : null;
  };
  // «наші, CPU» для парку — зважено, а не середнє відсотків: скільки процесів
  // реально зайняті з тих, що машини дозволяють. Середнє відсотків рівняло б
  // п'ятипроцесну машину з двадцятичотирипроцесною.
  const busyProcs = sum(live, w => (w.cpu_pct_ours != null && (w.threads || w.cores))
    ? w.cpu_pct_ours * (w.threads || w.cores) / 100 : 0);
  const allowedProcsAll = sum(live, w => w.procs_wanted || w.procs || 0);
  const tot = {
    cores: sum(live, w => w.cores),
    threads: sum(live, w => w.threads || w.cores),
    procs: sum(live, w => w.procs),
    cpu: avg(live, w => w.cpu_pct),
    oursPct: allowedProcsAll ? Math.min(100, 100 * busyProcs / allowedProcsAll) : null,
    memUsed: sum(live, w => w.mem_used_gb),
    memTotal: sum(live, w => w.mem_total_gb),
    memOurs: sum(live, w => w.mem_ours_gb),
    memBudget: sum(live, w => (w.mem_budget_gb != null ? w.mem_budget_gb : w.mem_avail_gb)),
    sessCombos: sum(workers, w => w.session_combos),
    combosAll: sum(workers, w => w.combos_total),
  };

  return (
    <div style={S.wrap}>
      {/* Заголовок «Кластер» + (i): пояснення ховається в підказку
          (менеджер 16.10), щоб не займало пів екрана. */}
      <h2 style={{ ...S.h2, display: 'flex', alignItems: 'center', gap: 8 }}>
        Кластер
        <InfoTip text={CLUSTER_HELP} />
        {viewer && <span style={{ ...S.badge, ...S.badgeCls.muted }}>перегляд</span>}
      </h2>

      {!viewer && <ClusterShareSettings />}

      {failuresFor && (
        <NodeFailuresModal worker={failuresFor} onClose={() => setFailuresFor(null)} />
      )}
      {commandFor && (
        <NodeCommandModal targets={commandFor} onClose={() => setCommandFor(null)}
                          onSent={() => load(true)} />
      )}

      {settingsFor && (
        <NodeSettingsModal targets={settingsFor} onClose={() => setSettingsFor(null)}
                           onSaved={() => { setPicked([]); load(true); }} />
      )}

      {error && <div style={S.err}>{error}</div>}

      {data && data.enabled === false && (
        <div style={S.warnBox}>
          Розподілені обчислення вимкнені на цьому сервері: {data.reason}
        </div>
      )}

      {/* Три блоки — 1 в 1 за текстом менеджера (29.10): на десктопі поруч,
          на телефоні переносяться один під одного (flex-wrap). */}
      <div style={S.summaryRow}>
        <div>
          {error && !data
            ? <div>Дані не отримано</div>
            : <div><b>{busyN}</b> машин рахують з <b>{workers.length}</b>
                {(localN || offlineN)
                  ? <> ({[localN ? `${localN} зайнята` : null,
                          offlineN ? `${offlineN} не на зв'язку` : null].filter(Boolean).join(', ')})</>
                  : null}
              </div>}
          {inFlight > 0 && <div><b>{n(inFlight)}</b> стратегій прораховується</div>}
          {sp && (
            <div><b>{n(sp.done)}</b> з <b>{n(sp.total)}</b> стратегій
              {sp.total ? <> (<b>{Math.floor(100 * sp.done / sp.total)}%</b>)</> : null} прораховано</div>
          )}
          {startedAt && (
            <div title="Скільки часу вже йде перебір">
              <b>{fmtStamp(startedAt)}</b> ({fmtDaysHours(nowSec - startedAt)}) час початку
            </div>
          )}
          {finishAt && (
            <div title="За середньою швидкістю всіх уже прорахованих стратегій цього перебору; у дужках — скільки ще лишилось">
              <b>{fmtStamp(finishAt)}</b> ({fmtDaysHours(finishAt - nowSec)}) час завершення
            </div>
          )}
          {data?.batches?.length > 1 && (
            <div>Сіток в оренді: <b>{data.batches.length}</b> (швидкість — першої)</div>
          )}
        </div>
        {sp?.avg_rate
          ? <div title="Добу/тиждень/місяць — прогноз за середньою швидкістю поточного перебору">
              <div><b>{sp.hour_rate ? n(sp.hour_rate * 3600) : '—'}</b> стратегій за останню годину</div>
              <div><b>{n(sp.avg_rate * 3600)}</b> стратегій середнє за годину</div>
              <div><b>{n(sp.avg_rate * 86400)}</b> стратегій за добу</div>
              <div><b>{n(sp.avg_rate * 7 * 86400)}</b> стратегій за тиждень</div>
              <div><b>{n(sp.avg_rate * 30 * 86400)}</b> стратегій за місяць</div>
            </div>
          : <div>Швидкість парку — <b>поки не міряна</b></div>}
        {/* Періоди (вікна) — рівна одиниця роботи: комбінація може вмерти на
            10-му вікні, а може пройти всі 1074. */}
        {sp?.win_avg_rate
          ? <div title={`Порахованих вікон перевірки з початку обліку: ${n(sp.win_total)}`}>
              <div><b>{sp.win_recent_rate ? n(sp.win_recent_rate * 3600) : '—'}</b> періодів за останню годину</div>
              <div><b>{n(sp.win_avg_rate * 3600)}</b> періодів середнє за годину</div>
              <div><b>{n(sp.win_avg_rate * 86400)}</b> періодів за добу</div>
              <div><b>{n(sp.win_avg_rate * 7 * 86400)}</b> періодів за тиждень</div>
              <div><b>{n(sp.win_avg_rate * 30 * 86400)}</b> періодів за місяць</div>
            </div>
          : (sp && <div>Періоди: <b>облік почався</b>, число зʼявиться за ~10 хв</div>)}
      </div>

      {!viewer && workers.length > 0 && (
        <div style={{ ...S.summary, alignItems: 'center' }}>
          <span>Вибрано <b>{picked.length}</b> з {workers.length}</span>
          <button style={{ ...S.btn, ...(picked.length ? S.btnStart : null) }}
                  disabled={!picked.length}
                  title="Повзунок, «Робота», пауза при людині й розклад — для всіх вибраних машин"
                  onClick={() => setSettingsFor(workers.filter(w => picked.includes(w.agent_id)))}>
            Налаштування вибраних…
          </button>
          <button style={{ ...S.btn, ...(picked.length ? S.btnStart : null) }}
                  disabled={!picked.length}
                  title="Перезапустити агента, перезавантажити чи вимкнути вибрані машини — де людина за машиною це дозволила"
                  onClick={() => setCommandFor(workers.filter(w => picked.includes(w.agent_id)))}>
            Перезапуск / вимкнення вибраних…
          </button>
        </div>
      )}

      {/* 🚨 ШАПКА ПРИЛИПАЄ ЛИШЕ ВСЕРЕДИНІ КОНТЕЙНЕРА З ПРОКРУТКОЮ. Контейнер із
          `overflow-x: auto` сам стає рамкою для `sticky`, тож прилипнути до
          верху СТОРІНКИ шапка не може за побудовою. Тому висота контейнера —
          екран, і рядки гортаються в ньому; шапка й «№ / Машина» стоять. */}
      <style>{HSCROLL_CSS}</style>
      <div>
      <div ref={tableRef} className="hscroll-hidden" style={{ ...S.tableWrap, ...S.tableWrapTall }}>
        <table style={S.table}>
          <thead>
            <tr>
              <th style={{ ...S.th, ...st1, position: 'sticky', top: 0,
                           background: S.th.background, zIndex: 4, textAlign: 'left',
                           boxShadow: S.thCornerShadow }}>
                {!viewer && workers.length > 0 && (
                  <input type="checkbox" title="Вибрати всі"
                         checked={picked.length === workers.length}
                         onChange={e => setPicked(e.target.checked ? workers.map(w => w.agent_id) : [])}
                         style={{ marginRight: 4 }} />
                )}
                <span onClick={() => {
                        setSort(null);
                        try { localStorage.removeItem(SORT_KEY); } catch (e) { /* не біда */ }
                      }}
                      style={{ cursor: sort ? 'pointer' : 'default' }}
                      title={sort ? 'Повернути порядок за замовчуванням' : ''}>№</span>
              </th>
              {sortTh('name', 'Машина', { ...stickM, position: 'sticky', top: 0,
                                          background: S.th.background, zIndex: 4,
                                          textAlign: 'left', boxShadow: S.thCornerShadow })}
              {sortTh('cpu', 'Процесор', { textAlign: 'left' })}
              {sortTh('state', 'Стан', null)}
              {sortTh('cpu_pct', 'CPU', null, 'Завантаженість машини цілком, разом із чужою роботою')}
              {sortTh('ours_cpu', 'Наші, CPU', null, 'Яку частку дозволеного нам процесора ми використовуємо. Дозволене — процеси, що дають ядра й повзунок на машині; 100% = усі вони зайняті')}
              {sortTh('mem_pct', 'Памʼять', null, 'Завантаженість памʼяті машини цілком, разом із чужими програмами')}
              {sortTh('ours_mem', 'Памʼять: наші / доступно нам', null, 'Скільки памʼяті ми використовуємо (агент + процеси розрахунку) з тієї, що машина дозволяє взяти НАМ: вільна памʼять без наших процесів мінус запас, лишений машині та людині. Саме цей бюджет, а не «усього», вирішує кількість процесів')}
              {sortTh('mem_total', 'Усього, ГБ', null, 'Уся памʼять машини')}
              {sortTh('started', 'Початок обрахунків', null)}
              {sortTh('secs', 'Час обрахунків', null)}
              {sortTh('sph', 'Стратегій/годину', null, 'Скільки стратегій (комбінацій параметрів) машина рахує за годину. Нижче — скільки пораховано за сесію і всього')}
              {sortTh('per1k', '1 тис. стратегій', null, 'Час на тисячу стратегій: першим кліком — найшвидші зверху')}
              {sortTh('wph', 'Періодів/годину', null, 'Скільки вікон перевірки (періодів) машина рахує за годину, СЕРЕДНЄ від початку обліку. Рівна одиниця роботи: на відміну від комбінацій, не залежить від того, скільки вікон пройде кожна. Показується після першої години: вікна приходять пачками')}
              {sortTh('per1kw', '1 тис. періодів', null, 'Час на тисячу періодів: першим кліком — найшвидші зверху')}
              <th style={{ ...S.th, textAlign: 'left' }}
                  title="Токен, яким машина підключена: стан, текст для перевстановлення агента, коли виданий">Токен</th>
              {!viewer && <th style={S.th}>Дії</th>}
            </tr>
          </thead>
          <tbody>
            {workers.length === 0 && orphanTokens.length === 0 && (
              <tr><td style={{ ...S.td, textAlign: 'center' }} colSpan={viewer ? 16 : 17}>
                {/* 🚨 «Жодна машина ще не підключалась» при недосяжному сервері
                    було БРЕХНЕЮ: машини є, ми просто не змогли їх спитати.
                    Спіймано на живому проді, коли сторінка лишалась відкритою
                    через перезбірку бекенду — банер червонів, а таблиця
                    впевнено повідомляла, що парку немає. */}
                {error && !data
                  ? 'Список не отримано — сервер зараз недоступний. Машини це '
                    + 'не зупиняє: вони працюють і зʼявляться, щойно звʼязок '
                    + 'відновиться.'
                  : 'Жодна машина ще не підключалась'}
              </td></tr>
            )}
            {rows.map((raw, i) => {
              // Вимкнена машина нічого не рахує й не займає: останні показники,
              // які вона надіслала перед тим, як замовкнути, — це минуле, і
              // «100 % / зайнято 56 ГБ» поруч із «Не на звʼязку» читались як
              // робота. Тож завантаженість і памʼять (наша й уся) — нулі.
              const w = shown(raw);
              const st = STATE[w.state] || STATE.idle;
              // 🚨 «Інші дані» має сенс лише для машини, яка рахує зі СВОГО
              // сховища. Вузол парку за домовленістю сховища не має — він
              // отримує комбінації разом зі знімком, тож розходитись йому нема
              // з чим, і червоний бейдж на ньому лякав би без причини.
              const fpBad = !w.snapshot_only && serverFp && w.fingerprint_id
                            && w.fingerprint_id !== serverFp;
              // 🚨 ДОЗВОЛЕНЕ, А НЕ ВСЯ МАШИНА. Процеси беруться по одному на
              // фізичне ядро, а завантаженість міряється від усіх потоків, тож
              // «з них наші 36 %» при стелі 50 % нічого не казало без
              // арифметики в голові. Тепер смуга = наша частка / частка, яку
              // дають ядра й повзунок (`procs_wanted` — до обмеження памʼяттю:
              // якщо памʼять урізала процеси, смуга це й покаже).
              const { allowedProcs, threads, cpuAllowedPct, cpu: cpuOfAllowed,
                      memBudget, mem: memOfAllowed } = ofAllowed(w);
              const memCut = w.procs_wanted && w.procs && w.procs < w.procs_wanted;
              return (
                <tr key={w.agent_id} style={S.tr}>
                  <td style={{ ...S.td, ...st1, whiteSpace: 'nowrap' }}>
                    {!viewer && (
                      <input type="checkbox" checked={picked.includes(w.agent_id)}
                             onChange={e => setPicked(p => e.target.checked
                               ? [...p, w.agent_id] : p.filter(x => x !== w.agent_id))}
                             style={{ marginRight: 4 }} />
                    )}
                    {i + 1}
                  </td>
                  <td style={{ ...S.td, ...stickM }}>
                    <div style={{ fontWeight: 600 }}>{w.name}</div>
                    <div style={S.sub}>
                      <div>
                      {w.cores} ядер{w.threads > w.cores ? ` (${w.threads} потоків)` : ''} · {w.procs} процесів
                      {/* Причина стоїть поруч із наслідком. «14 процесів на 24
                          ядрах» без цього читається як недоробка, хоча це
                          рішення людини за самою машиною: повзунок «віддавати
                          машину» стоїть у неї на 60 %. */}
                      {w.throttle_pct != null && w.throttle_pct < 100 && (
                        <span title={'Людина за цією машиною віддала парку '
                                     + `${w.throttle_pct}% — решта ядер лишається їй. `
                                     + 'Повзунок — у вікні агента на машині або тут, кнопкою ⚙.'}>
                          {' '}· віддано {w.throttle_pct}%
                        </span>
                      )}
                      </div>
                      {w.procs_note && (
                        <span title={'Агент міряє памʼять одного процесу під цю сітку й не бере '
                                     + 'більше процесів, ніж машина вміщує: інакше система вбиває '
                                     + 'процес рахунку і весь блок пропадає. '
                                     + (w.mem_per_proc_gb ? `Зараз ${w.mem_per_proc_gb} ГБ на процес.` : '')}
                              style={{ display: 'block', color: '#B45309' }}>
                          {w.procs_note}
                        </span>
                      )}
                      <div style={S.badgeRow}>
                      {Object.values(w.settings?.admin || {}).some(x => x.state === 'pending') && (
                        <span style={{ ...S.badge, ...S.badgeCls.warn, }}
                              title={w.settings?.supported
                                     ? 'Правка з адмінки ще не дійшла до машини — візьме на найближчому стуку'
                                     : 'Агент старший за випуск 69: правку візьме після оновлення'}>
                          налаштування чекають
                        </span>
                      )}
                      {w.snapshot_only && (
                        <span style={{ ...S.badge, ...S.badgeCls.muted, }}
                              title={"Машина не має власного сховища котирувань: "
                                     + "дані приїжджають від сервера разом із комбінаціями"}>
                          дані з сервера
                        </span>
                      )}
                      {w.revoked && (
                        <span style={{ ...S.badge, ...S.badgeCls.bad, }}
                              title={w.revoked}>
                          токен відкликано
                        </span>
                      )}
                      {w.needs_update && (
                        <span style={{ ...S.badge,
                                       ...(UPDATE_PLAN[w.update_plan]?.bad ? S.badgeCls.bad : S.badgeCls.warn) }}
                              title={UPDATE_PLAN[w.update_plan]?.hint
                                     || w.update_message
                                     || 'Версія агента на машині не збігається з чинною'}>
                          версія {w.agent_version || '—'}
                          {serverAgentV && w.update_plan !== 'server' ? ` → ${serverAgentV}` : ''}
                        </span>
                      )}
                      {/* 🚨 Чому машина не оновилась САМА. Без цього «не змогла»
                          і «їй ще не виклали випуск» виглядають однаково, і
                          єдина відповідь лежить у лозі на самій машині —
                          куди на офісному парку не полізе ніхто (03.09). */}
                      {w.selfupdate_error && (
                        <span style={{ ...S.badge, ...S.badgeCls.bad, }}
                              title={`Самооновлення не вдалось: ${w.selfupdate_error}`}>
                          не оновилась сама
                        </span>
                      )}
                      {/* Комбінації, які машина здала БЕЗ результату, і чому
                          востаннє. До 10.10 причину бачило лише вікно агента на
                          самій машині, і №424 крутилась на TAKITOPC ~4 год. */}
                      {w.fail_count > 0 && w.last_failure && (
                        <span style={{ ...S.badge, ...S.badgeCls.warn,
                                       ...(viewer ? null : { cursor: 'pointer', textDecoration: 'underline' }) }}
                              onClick={viewer ? undefined : () => setFailuresFor(w)}
                              title={(viewer ? '' : 'Клік — журнал збоїв цієї машини. ')
                                     + `Без результату: ${w.fail_count} комбінацій. `
                                     + `Востаннє №${w.last_failure.combo_index}`
                                     + (w.last_failure.n > 1 ? ` (+${w.last_failure.n - 1} у тому блоці)` : '')
                                     + ` — ${w.last_failure.reason}`}>
                          збої: {w.fail_count}
                        </span>
                      )}
                      {fpBad && (
                        <span style={{ ...S.badge, ...S.badgeCls.bad, }}
                              title={`Дані машини (${w.fingerprint_id}) не збігаються `
                                     + `з серверними (${serverFp}). Роботи вона не отримає.`}>
                          інші дані
                        </span>
                      )}
                      </div>
                    </div>
                  </td>
                  <td style={S.tdCpu} title={w.cpu}>{shortCpu(w.cpu)}</td>
                  <td style={S.tdC}>
                    <span style={{ ...S.badge, ...S.badgeCls[st.cls] }}>{st.text}</span>
                    {w.state === 'offline' && w.silent_secs != null && (
                      <div style={S.sub}>мовчить {fmtDur(w.silent_secs)}</div>
                    )}
                    {w.state === 'quarantined' && w.quarantine_reason && (
                      <div style={S.sub}>{w.quarantine_reason}</div>
                    )}
                    {w.state === 'local' && w.local_idle && (
                      <div style={S.sub}>{w.local_idle}</div>
                    )}
                    {w.needs_update && UPDATE_PLAN[w.update_plan] && (
                      <div style={{ ...S.sub,
                                    color: UPDATE_PLAN[w.update_plan].bad ? '#C02B5E' : '#B45309' }}
                           title={UPDATE_PLAN[w.update_plan].hint}>
                        {UPDATE_PLAN[w.update_plan].line}
                      </div>
                    )}
                    {cmdLine(w.command) && (
                      <div style={{ ...S.cmdSub, color: w.command.state === 'failed' ? '#C02B5E' : '#B45309' }}
                           title={w.command.msg || ''}>
                        {cmdLine(w.command)}
                      </div>
                    )}
                    {w.lease && (
                      <div style={S.sub}>
                        блок [{w.lease.from}:{w.lease.to})
                        {w.block_secs > 0 && ` · ${fmtDur(w.block_secs)}`}
                        {w.extra_blocks > 0 && (
                          <span title="Процеси, що звільнились у хвості блоку, взяли ще роботу цієї сітки">
                            {` · +${w.extra_blocks} добір`}
                          </span>
                        )}
                      </div>
                    )}
                  </td>
                  <td style={S.tdC}><Bar pct={w.cpu_pct} /></td>
                  <td style={S.tdC}
                      title={`Наш агент зараз бере ${w.cpu_pct_ours != null ? Math.round(w.cpu_pct_ours) : '—'}% `
                             + `процесора машини; `
                             + (memCut
                                ? `працює ${w.procs} процесів: повзунок дозволяє ${w.procs_wanted} із `
                                  + `${threads || '—'} потоків (${cpuAllowedPct ? Math.round(cpuAllowedPct) : '—'}% машини), `
                                  + `але бракує памʼяті.`
                                : `дозволено ${allowedProcs || '—'} процесів із `
                                  + `${threads || '—'} потоків (${cpuAllowedPct ? Math.round(cpuAllowedPct) : '—'}% машини).`)
                             + cpuDiagText(w.cpu_diag)}>
                    <Bar pct={cpuOfAllowed} />
                    {allowedProcs != null && (
                      <div style={S.sub}>
                        {memCut ? `${w.procs} з ${w.procs_wanted} процесів · обмежує памʼять`
                                : `${allowedProcs} процесів дозволено`}
                      </div>
                    )}
                    {cpuDiagWarn(w.cpu_diag) && (
                      <div style={{ ...S.sub, color: '#B45309' }}>{cpuDiagWarn(w.cpu_diag)}</div>
                    )}
                  </td>
                  <td style={S.tdC}><Bar pct={w.mem_pct} danger />
                    <div style={S.sub} title="Памʼять, зайнята на всій машині: система, чужі програми й наш агент разом">зайнято {fmtGb(w.mem_used_gb)} з {fmtGb(w.mem_total_gb)} ГБ</div>
                  </td>
                  {/* 🚨 ТРИ ЧИСЛА, А НЕ ОДНЕ. Питання власника 23.09 звучало
                      так: «стеля 38 %, зайнято 10.2 з 30 ГБ — чому не більше
                      процесів?». Обидва числа на екрані були машинні, а
                      рішення приймається по НАШОМУ бюджету: вільно без наших
                      дітей мінус запас, поділене на вагу процесу. Поки цих
                      чисел не було видно, сторінка сама виглядала як помилка. */}
                  <td style={S.tdC}
                      title={w.mem_per_proc_gb
                             ? `План: ${w.mem_per_proc_gb} ГБ на процес (з запасом ×1.2) → `
                               + `${w.procs} процесів`
                               + (w.procs_wanted ? ` з ${w.procs_wanted}, що дозволяють ядра й повзунок` : '')
                               + (w.mem_reserve_gb != null ? `. Машині лишено ${w.mem_reserve_gb} ГБ` : '')
                             : 'Бюджет рахується в мить видачі блоку — поки машина не рахувала, показано вільну памʼять'}>
                    <Bar pct={memOfAllowed} danger />
                    <div style={S.sub}>
                      {fmtGb(w.mem_ours_gb)} з {fmtGb(memBudget)} ГБ
                      {w.mem_budget_gb == null && memBudget != null ? ' вільних' : ''}
                    </div>
                  </td>
                  <td style={S.tdC}>{fmtGb(w.mem_total_gb)}</td>
                  <td style={S.tdC}>{fmtClock(w.session_started)}</td>
                  <td style={S.tdC}>{fmtDur(w.session_secs)}</td>
                  <td style={S.tdC}>
                    {perHour(w.secs_per_1k) != null ? perHour(w.secs_per_1k).toLocaleString('uk-UA') : '—'}
                    <div style={S.sub}>за сесію {(w.session_combos || 0).toLocaleString('uk-UA')}</div>
                    {/* Блок у роботі показуємо окремо: на важкій сітці він іде
                        пів години, і без цього рядка машина, яка чесно рахує,
                        виглядає як зупинена — «0 комбінацій, 0 с». */}
                    {w.block_size > 0 && (
                      <div style={S.sub}>+{w.block_size} у роботі</div>
                    )}
                    <div style={S.sub}>усього {(w.combos_total || 0).toLocaleString('uk-UA')}</div>
                  </td>
                  <td style={S.tdC}>{fmtPer1k(w.secs_per_1k)}</td>
                  <td style={S.tdC}
                      title={'Середнє від початку обліку. Вікна приходять пачками: комбінація, що '
                             + 'пройшла відсів, рахує всі вікна десятки хвилин і здається одним '
                             + 'шматком, тож число стає надійним за кілька годин.'}>
                    {w.windows_per_hour != null ? w.windows_per_hour.toLocaleString('uk-UA') : '—'}
                    {w.procs > 0 && w.windows_per_hour != null && (
                      <div style={S.sub}>{Math.round(w.windows_per_hour / w.procs).toLocaleString('uk-UA')} на процес</div>
                    )}
                    {w.windows_hours != null && (
                      <div style={S.sub}>
                        {w.windows_per_hour != null
                          ? `середнє за ${w.windows_hours} год`
                          : `міряється (${w.windows_hours} з 1 год)`}
                      </div>
                    )}
                  </td>
                  <td style={S.tdC}>{fmtPer1k(secsPer1k(w.windows_per_hour))}</td>
                  <td style={S.tdTok}>
                    {(tokByAgent[w.agent_id] || []).length
                      ? tokByAgent[w.agent_id].map(tk => renderTok(tk, tk.label !== w.name))
                      : <span style={S.sub}
                              title="Машина підключена спільним секретом сервера, без окремого токена">—</span>}
                  </td>
                  {!viewer && (
                  <td style={{ ...S.tdC, whiteSpace: 'nowrap' }}>
                    <button
                      onClick={() => toggle(w)}
                      disabled={busyIds.includes(w.agent_id)}
                      style={{ ...S.btn,
                               ...((w.paused || w.quarantined) ? S.btnStart : S.btnStop) }}
                    >
                      {(w.paused || w.quarantined) ? 'Запустити' : 'Зупинити'}
                    </button>
                    <button onClick={() => setSettingsFor([raw])}
                            title="Налаштування машини: повзунок, «Робота», пауза при людині, розклад"
                            style={{ ...S.btn, ...S.btnGhost }}>
                      ⚙
                    </button>
                    <button onClick={() => setCommandFor([raw])}
                            title="Перезапустити агента, перезавантажити чи вимкнути машину (якщо людина за нею дозволила)"
                            aria-label="Перезапуск і живлення"
                            style={{ ...S.btn, ...S.btnGhost, verticalAlign: 'middle' }}>
                      {/* SVG, а не символ ⏻: у частині шрифтів (старі Windows,
                          Android) для нього немає гліфа — виходив порожній квадрат. */}
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                           strokeWidth="2.4" strokeLinecap="round" style={{ display: 'block' }}>
                        <path d="M12 3v9" /><path d="M6.3 6.8a8 8 0 1 0 11.4 0" />
                      </svg>
                    </button>
                    {/* Машина більше не зникає сама (запис вузла без терміну
                        життя) — прибирає її лише цей хрестик. */}
                    <button onClick={() => forget(w)} title="Прибрати машину зі списку"
                            style={{ ...S.btn, ...S.btnGhost, ...S.btnX }}>
                      ✕
                    </button>
                  </td>
                  )}
                </tr>
              );
            })}
            {/* Токени без машини в списку: ще не використані або машину
                прибрали хрестиком. Окремої таблиці токенів більше немає. */}
            {orphanTokens.map(tk => (
              <tr key={`tok-${tk.id}`} style={{ ...S.tr, background: '#FCFCFD' }}>
                <td style={{ ...S.td, ...st1, background: '#FCFCFD' }}></td>
                <td style={{ ...S.td, ...stickM, background: '#FCFCFD' }}>
                  <div style={{ fontWeight: 600 }}>{tk.label}</div>
                  <div style={{ ...S.sub, wordBreak: 'break-all' }}>{tk.id}</div>
                </td>
                <td style={S.tdCpu}></td>
                <td style={S.tdC}>
                  <span style={{ ...S.badge, ...S.badgeCls.muted }}>
                    {tk.bound_agent ? 'Немає в списку машин' : 'Ще не підключалась'}
                  </span>
                  {tk.bound_agent && (
                    <div style={S.sub}>{tk.bound_name || tk.bound_agent}</div>
                  )}
                  {tk.last_used && <div style={S.sub}>востаннє {fmtClock(tk.last_used)}</div>}
                </td>
                {Array.from({ length: 11 }).map((_, k) => <td key={k} style={S.tdC}></td>)}
                <td style={S.tdTok}>{renderTok(tk, false)}</td>
                {!viewer && <td style={S.tdC}></td>}
              </tr>
            ))}
          </tbody>
          {workers.length > 0 && (
            <tfoot>
              <tr>
                <td style={{ ...S.tfootTd, ...st1, background: S.tfootTd.background }}></td>
                <td style={{ ...S.tfootTd, ...stickM, background: S.tfootTd.background, textAlign: 'left' }}>
                  Разом: {live.length} на звʼязку
                  <div style={S.sub}>{tot.cores} ядер · {tot.threads} потоків · {tot.procs} процесів</div>
                </td>
                <td style={S.tfootTd}></td>
                <td style={S.tfootTd}>{busyN} рахують</td>
                <td style={S.tfootTd} title="Середня завантаженість машин на звʼязку">
                  <Bar pct={tot.cpu} /><div style={S.sub}>середнє</div>
                </td>
                <td style={S.tfootTd}
                    title="Скільки процесів парку реально зайняті з тих, що машини дозволяють (зважено за кількістю процесів)">
                  <Bar pct={tot.oursPct} />
                  <div style={S.sub}>~{Math.round(busyProcs)} з {allowedProcsAll} процесів</div>
                </td>
                <td style={S.tfootTd}>
                  <Bar pct={tot.memTotal ? 100 * tot.memUsed / tot.memTotal : null} danger />
                  <div style={S.sub}>зайнято {fmtGb(tot.memUsed)} з {fmtGb(tot.memTotal)} ГБ</div>
                </td>
                <td style={S.tfootTd}>
                  <Bar pct={tot.memBudget ? 100 * tot.memOurs / tot.memBudget : null} danger />
                  <div style={S.sub}>{fmtGb(tot.memOurs)} з {fmtGb(tot.memBudget)} ГБ</div>
                </td>
                <td style={S.tfootTd}>{fmtGb(tot.memTotal)}</td>
                <td style={S.tfootTd}></td>
                <td style={S.tfootTd}></td>
                <td style={S.tfootTd} title="Парк цілком, за лічильником вирішених комбінацій сітки">
                  {sp?.avg_rate ? n(Math.round(sp.avg_rate * 3600)) : '—'}
                  <div style={S.sub}>за сесію {n(tot.sessCombos)}</div>
                  {inFlight > 0 && <div style={S.sub}>+{n(inFlight)} у роботі</div>}
                  <div style={S.sub}>усього {n(tot.combosAll)}</div>
                </td>
                <td style={S.tfootTd} title="Парк цілком, за лічильником вирішених комбінацій сітки">
                  {sp?.avg_rate ? fmtPer1k(1000 / sp.avg_rate) : '—'}
                  <div style={S.sub}>середнє парку</div>
                </td>
                <td style={S.tfootTd} title="Сума по машинах на звʼязку">
                  {n(sum(live, w => w.windows_per_hour))}
                  <div style={S.sub}>сума машин</div>
                </td>
                <td style={S.tfootTd}>{fmtPer1k(secsPer1k(sum(live, w => w.windows_per_hour)))}</td>
                <td style={S.tfootTd}>{tokens.length ? `${tokens.length} токенів` : ''}</td>
                {!viewer && <td style={S.tfootTd}></td>}
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      <StickyHScroll target={tableRef} />
      </div>

      <p style={S.foot}>
        Оновлюється щоп'ять секунд. Машина, що замовкла довше ніж на півтори
        хвилини, позначається «не на звʼязку»; блок, який вона тримала,
        повертається в чергу через пʼять хвилин і дістається іншій. Зі списку
        машина сама не зникає — лише після «✕».
      </p>

      <h3 style={{ ...S.h3, display: 'flex', alignItems: 'center', gap: 8 }}>
        Пакети агента
        <InfoTip text={<>
          <p style={S.tipP}>
            Те, що ставиться на машину. Пакети самодостатні — ні Python, ні
            бібліотек ставити не треба, інтернет на машині потрібен лише щоб
            зʼєднатися з цим сервером. Код агента далі оновлюється сам; пакет
            потрібен лише щоб поставити машину вперше або перевести її на службу.
          </p>
          <p style={{ ...S.tipP, marginBottom: 0 }}>
            Людині за майбутньою машиною давати доступ сюди не треба — дайте їй
            сторінку <code style={{ wordBreak: 'break-all' }}>{(window.location.origin || '') + '/api/agent/page'}</code> і
            токен. Там ті самі пакети й ті самі три кроки, без кабінету.
          </p>
        </>} />
      </h3>

      {/* 🚨 ІНСТРУКЦІЯ = ТЕ, ЩО РЕАЛЬНО ПРАЦЮЄ З ЦИМ ПАКЕТОМ. Старий текст
          («поставити → відкрити → токен») описував запуск ПІСЛЯ входу людини;
          з v57 Windows ставиться службою й стартує до входу. Скрипти служби
          є лише в пакеті v57+, самооновлення їх не доставляє (воно несе код,
          не файли запуску) — тому для вже поставлених машин окремий порядок. */}
      <div style={S.howto}>
        <div style={S.howtoCol}>
          <b>Windows — нова машина</b>
          <ol style={S.ol}>
            <li>Скачати пакет Windows і розпакувати в постійну теку,
              напр. <code>C:\algo-agent</code>. Теку потім не переносити.</li>
            <li>Правою кнопкою по <code>install-service.bat</code> →
              «Запуск від імені адміністратора». Агент стане службою:
              стартує разом із Windows, ще до входу в систему.</li>
            <li>Ярлик <b>«Algo Agent»</b> на робочому столі → вставити токен →
              «Підключити».</li>
          </ol>
          <div style={S.sub}>
            Немає прав адміністратора — <code>run-agent.bat</code> і
            <code> install-autostart.bat</code>: працюватиме, але лише після
            входу користувача.
          </div>
        </div>
        <div style={S.howtoCol}>
          <b>Windows — машина, поставлена до версії 57</b>
          <ol style={S.ol}>
            <li>У старій теці агента — <code>stop-agent.bat</code>.</li>
            <li>Скачати пакет Windows і розпакувати в <b>нову</b> теку.</li>
            <li>У новій теці: <code>install-service.bat</code> → «Запуск від
              імені адміністратора». Токен, налаштування й знімок котирувань
              переїдуть самі — нічого не вводити.</li>
            <li>Перевірити: значок біля годинника, ярлик «Algo Agent», машина
              тут на звʼязку. Стару теку можна видалити.</li>
          </ol>
        </div>
        <div style={S.howtoCol}>
          <b>Linux</b>
          <ol style={S.ol}>
            <li>Покласти поруч пакет Linux і <code>install-linux.sh</code>,
              виконати <code>sh install-linux.sh</code> (потрібен лише Docker).
              Стартує з системою сам.</li>
            <li>Відкрити <code>http://127.0.0.1:8787</code> на тій машині →
              вставити токен.</li>
          </ol>
        </div>
      </div>

      {dist && dist.stale && (
        // 🚨 ТРИВОГА — лише коли машина СПРАВДІ застрягне: пакет старіший за
        // сервер, а випуску для самооновлення немає. Тоді поставлений вузол
        // так і стоятиме на «потрібне оновлення».
        <p style={S.stale}>
          Пакети на роздачі — версія {dist.version}, координатор вимагає{' '}
          {dist.server_version}, а випуску для самооновлення на сервері немає.
          Поставлена звідси машина застрягне. Перезберіть роздачу:{' '}
          <code>python scripts/build_agent_dist.py --out dist</code>
        </p>
      )}
      {dist && dist.behind && !dist.stale && (
        // Пакет несе БІБЛІОТЕКИ, самооновлення несе КОД — тож відставання
        // саме собою машину не ламає: вона дожене версію за хвилину (в полі
        // LEGION пройшла 13 → 29 без жодного дотику). Це довідка, а не тривога:
        // повідомлення, яке зʼявляється тоді, коли робити нічого не треба,
        // привчає його не читати.
        <p style={S.hint}>
          Пакети на роздачі — версія {dist.version}, поточна {dist.server_version}.
          Нічого робити не треба: машина, поставлена звідси, дотягне свіжий код
          сама за хвилину. Перезбирати роздачу варто, коли міняються бібліотеки.
        </p>
      )}
      {dist && dist.available ? (
        <div style={S.distRow}>
          {['windows', 'linux', 'install-linux.sh'].map(key => {
            const f = (dist.files || {})[key];
            if (!f) return null;
            const label = key === 'windows' ? 'Windows'
                        : key === 'linux' ? 'Linux (Docker)'
                        : 'install-linux.sh';
            return (
              <div key={key} style={S.distCard}>
                <div style={{ fontWeight: 600, marginBottom: 2 }}>{label}</div>
                <div style={S.sub}>
                  версія {dist.version} · {(f.bytes / 1048576).toFixed(0)} МБ
                </div>
                <button
                  onClick={() => download(f.file)}
                  disabled={downloading === f.file}
                  style={{ ...S.btn, ...S.btnStart, marginTop: 8 }}
                >
                  {downloading === f.file ? 'Качаю…' : 'Скачати'}
                </button>
              </div>
            );
          })}
        </div>
      ) : (
        <div style={S.warnBox}>
          Пакети ще не викладені. Зберіть їх командою
          <code> python scripts/build_agent_dist.py</code> і покладіть вміст
          у теку <code>{dist?.dist_dir || 'dist/'}</code> на координаторі.
        </div>
      )}

      <h3 style={{ ...S.h3, display: 'flex', alignItems: 'center', gap: 8 }}>
        Токени машин
        <InfoTip text={<p style={{ ...S.tipP, marginBottom: 0 }}>
          Щоб додати машину, видайте їй токен і передайте людині, яка її
          обслуговує: вона вставляє його один раз у вікні агента
          (<code>http://127.0.0.1:8787</code> або ярлик «Algo Agent»). Токен
          закріплюється за машиною при першому підключенні й далі чинний лише
          для неї. «Відкликати» діє одразу — вузол втрачає роботу на найближчому
          зверненні, а не після перезапуску. Токени видно в колонці «Токен»
          таблиці машин; ще не використані — окремими рядками внизу таблиці.
        </p>} />
      </h3>

      <div style={S.addRow}>
        <input
          value={newLabel}
          onChange={e => setNewLabel(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') addMachine(); }}
          placeholder="Назва машини, напр. FX-8100 №7"
          style={S.input}
        />
        <button onClick={addMachine} style={{ ...S.btn, ...S.btnStart }}>
          Додати машину
        </button>
      </div>

      {freshToken && (
        <div style={S.tokenBox}>
          <div style={{ marginBottom: 6 }}>
            Токен для «<b>{freshToken.label}</b>». Його також можна буде
            скопіювати з колонки «Токен» таблиці машин, якщо агента доведеться
            ставити заново.
          </div>
          <code style={S.tokenCode}>{freshToken.token}</code>
          <div style={S.addRow}>
            <button
              onClick={() => { navigator.clipboard?.writeText(freshToken.token); }}
              style={{ ...S.btn, ...S.btnGhost }}
            >
              Скопіювати
            </button>
            <button onClick={() => setFreshToken(null)} style={{ ...S.btn, ...S.btnGhost }}>
              Приховати
            </button>
          </div>
        </div>
      )}

    </div>
  );
}

// З чого агент склав «наші, CPU» (агент 79+). Попередження — лише коли частину
// процесів пулу дерево процесів не знайшло або їх не вдалось прочитати: тоді
// число «наші» до агента 79 було занижене (WORKSTATION 02.11: 45 % замість ~90).
function cpuDiagText(d) {
  if (!d) return '';
  let t = ` У підрахунку ${d.tree ?? '—'} процесів агента, у пулі ${d.pool ?? '—'}`;
  if (d.pool_outside_tree) t += `, з них ${d.pool_outside_tree} поза деревом процесів`;
  if (d.unreadable) t += `; не прочитано ${d.unreadable}${d.unreadable_err ? ` (${d.unreadable_err})` : ''}`;
  return t + '.';
}

function cpuDiagWarn(d) {
  if (!d) return '';
  const parts = [];
  if (d.pool_outside_tree) parts.push(`${d.pool_outside_tree} процесів поза деревом`);
  if (d.unreadable) parts.push(`не прочитано ${d.unreadable}`);
  return parts.join(' · ');
}

const S = {
  // 1700, а не 1500: виміряно в браузері — таблиця з 13 колонок займає
  // ~1420 px, і при вужчому контейнері «Стан» із кнопкою ховались за
  // горизонтальний скрол навіть на моніторі 1920.
  wrap:  { maxWidth: 1800, margin: '0 auto', padding: '24px 16px', color: '#1A1A1A' },
  h2:    { margin: '0 0 8px', fontSize: 22, color: '#1A1A1A' },
  h3:    { margin: '32px 0 8px', fontSize: 18, color: '#1A1A1A' },
  lead:  { margin: '0 0 16px', color: '#5B646F', lineHeight: 1.5, maxWidth: 900 },
  addRow: { display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12,
            flexWrap: 'wrap' },
  hint:  { margin: '0 0 16px', padding: '10px 12px', borderRadius: 8,
           background: '#F4F6F8', color: '#5B646F', border: '1px solid #E3E8EE',
           lineHeight: 1.5, maxWidth: 900 },
  stale: { margin: '0 0 16px', padding: '10px 12px', borderRadius: 8,
           background: '#FDF2F5', color: '#8A1E42', border: '1px solid #F3C6D4',
           lineHeight: 1.5, maxWidth: 900 },
  howto:    { display: 'flex', flexWrap: 'wrap', gap: 12, margin: '4px 0 14px' },
  howtoCol: { flex: '1 1 260px', background: '#F8F9FB', border: '1px solid #E5E7EB',
              borderRadius: 8, padding: '10px 14px', fontSize: 13.5, lineHeight: 1.5 },
  ol:       { margin: '6px 0 6px 18px', padding: 0 },
  distRow: { display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 8 },
  distCard: { border: '1px solid #E5E7EB', borderRadius: 8, padding: '12px 14px',
              background: '#FFFFFF', minWidth: 190 },
  input: { padding: '7px 10px', border: '1px solid #E5E7EB', borderRadius: 6,
           fontSize: 13, minWidth: 280 },
  // Токен показується один раз, тож блок навмисно помітний: пропустити його
  // означає видавати токен наново.
  tokenBox: { border: '1px solid #FCD34D', background: '#FFFBEB', borderRadius: 8,
              padding: '12px 14px', marginBottom: 16, maxWidth: 900,
              color: '#78350F', fontSize: 13 },
  tokInline: { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 12,
               wordBreak: 'break-all', color: '#1A1A1A' },
  tokenCode: { display: 'block', padding: '8px 10px', background: '#FFFFFF',
               border: '1px solid #E5E7EB', borderRadius: 6, marginBottom: 8,
               fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
               fontSize: 13, wordBreak: 'break-all', color: '#1A1A1A' },
  summaryRow: { display: 'flex', flexWrap: 'wrap', columnGap: 48, rowGap: 12, fontSize: 13,
                color: '#5B646F', marginBottom: 16, lineHeight: 1.6 },
  summary: { display: 'flex', gap: 20, flexWrap: 'wrap', fontSize: 13,
             color: '#5B646F', marginBottom: 12 },
  tableWrap: { overflowX: 'auto', WebkitOverflowScrolling: 'touch',
               overscrollBehaviorX: 'contain', border: '1px solid #E5E7EB', borderRadius: 8,
               background: '#FFFFFF' },
  // Висота — екран без верхнього меню: шапка таблиці прилипає всередині.
  tableWrapTall: { overflowY: 'auto', maxHeight: 'max(360px, calc(100vh - 90px))' },
  tdTok:   { padding: '8px 8px', color: '#1A1A1A' },
  tokCell: { padding: '2px 0', whiteSpace: 'nowrap' },
  tokLine: { display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'nowrap' },
  btnSm:   { padding: '2px 8px', fontSize: 11.5 },
  cmdSub:  { fontSize: 11, marginTop: 2, whiteSpace: 'normal', maxWidth: 200,
             marginLeft: 'auto', marginRight: 'auto', lineHeight: 1.35 },
  tipP:    { margin: '0 0 8px' },
  // «№» і «Машина» лишаються на місці, коли таблицю гортають вправо: інакше
  // на вузькому екрані в колонках «Стан» і «1 тис. комбінацій» не видно, чиї
  // це числа. Фон непрозорий — під ним проїжджають інші колонки.
  stick1: { position: 'sticky', left: 0, zIndex: 2, background: '#FFFFFF',
            minWidth: 36, width: 36, boxSizing: 'border-box' },
  // Ширина фіксована, текст переноситься: підпис машини (ядра, памʼять,
  // бейджі) раніше йшов одним рядком на ~700 px.
  stick2: { position: 'sticky', left: 36, zIndex: 2, background: '#FFFFFF',
            boxShadow: '1px 0 0 #E5E7EB', whiteSpace: 'normal',
            width: 220, minWidth: 220, maxWidth: 220 },
  stick2Mobile: { width: 140, minWidth: 140, maxWidth: 140, fontSize: 12 },
  badgeRow: { display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 3 },
  tfootTd: { padding: '10px 8px', background: '#F9FAFB', color: '#1A1A1A',
             borderTop: '2px solid #E5E7EB', whiteSpace: 'nowrap',
             fontWeight: 600, textAlign: 'center', verticalAlign: 'top' },
  // Мінімальна ширина, інакше браузер тисне колонки до нечитабельного:
  // назва процесора ламалась на шість рядків, а рядок ставав заввишки з абзац.
  // 🚨 `separate`, А НЕ `collapse` (29.10, Android Chrome): прилиплі клітинки
  // у таблиці з collapse під час прокрутки переставали перемальовуватись —
  // рядки й цілі колонки зникали. Межі рядків тепер на клітинках (у separate
  // межа <tr> не малюється) — див. HSCROLL_CSS.
  table: { width: '100%', minWidth: 1100, borderCollapse: 'separate', borderSpacing: 0,
           fontSize: 13 },
  // Шапка прилипає до верху контейнера таблиці (див. tableWrapTall). Тінь
  // замість border-bottom: межа клітинки при border-collapse з sticky зникає.
  th:    { padding: '10px 8px', background: '#F9FAFB', color: '#5B646F',
           whiteSpace: 'nowrap', boxShadow: 'inset 0 -1px 0 #E5E7EB',
           fontWeight: 600, textAlign: 'center',
           position: 'sticky', top: 0, zIndex: 3 },
  thCornerShadow: 'inset 0 -1px 0 #E5E7EB, 1px 0 0 #E5E7EB',
  sortMark: { color: '#9AA3AF', fontSize: 11 },
  tr:    { borderBottom: '1px solid #F1F3F5' },
  td:    { padding: '8px 8px', color: '#1A1A1A', whiteSpace: 'nowrap',
           fontVariantNumeric: 'tabular-nums' },
  tdC:   { padding: '8px 8px', color: '#1A1A1A', whiteSpace: 'nowrap',
           textAlign: 'center', fontVariantNumeric: 'tabular-nums' },
  tdCpu: { padding: '8px 8px', color: '#1A1A1A', whiteSpace: 'normal',
           minWidth: 160, maxWidth: 200 },
  sub:   { fontSize: 11, color: '#8A93A0', marginTop: 2 },
  badge: { padding: '2px 8px', borderRadius: 4, fontSize: 12, whiteSpace: 'nowrap',
           fontWeight: 500, display: 'inline-block' },
  badgeCls: {
    ok:    { background: '#E7F7EF', color: '#007A41' },
    warn:  { background: '#FEF3C7', color: '#92400E' },
    bad:   { background: '#FDE8EF', color: '#C02B5E' },
    muted: { background: '#F1F3F5', color: '#5B646F' },
  },
  barRow:  { display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'center' },
  barWrap: { position: 'relative', width: 56, height: 8, borderRadius: 4,
             background: '#F1F3F5', overflow: 'hidden' },
  barFill: { position: 'absolute', left: 0, top: 0, bottom: 0, borderRadius: 4 },
  barText: { fontSize: 12, color: '#1A1A1A', minWidth: 32, textAlign: 'right',
             fontVariantNumeric: 'tabular-nums' },
  btn:   { padding: '5px 12px', borderRadius: 6, border: '1px solid #E5E7EB',
           fontSize: 12.5, cursor: 'pointer', whiteSpace: 'nowrap' },
  btnStop:  { background: '#FFFFFF', color: '#C02B5E', borderColor: '#F5C2D3' },
  btnStart: { background: '#475FFA', color: '#fff', borderColor: '#475FFA' },
  btnGhost: { background: '#FFFFFF', color: '#5B646F', marginLeft: 6 },
  btnX:     { padding: '4px 9px', border: '1px solid #E5E7EB' },
  info:  { padding: 16, color: '#5B646F' },
  foot:  { marginTop: 12, fontSize: 12, color: '#8A93A0', maxWidth: 900, lineHeight: 1.5 },
  err:   { padding: 12, background: '#FDE8EF', border: '1px solid #F5C2D3',
           borderRadius: 6, color: '#C02B5E', marginBottom: 12 },
  warnBox: { padding: 12, background: '#FEF3C7', border: '1px solid #FDE68A',
             borderRadius: 6, color: '#92400E', marginBottom: 12 },
};
